/**
 * Customer Value (LTV) — "How do we increase our customer LTV over time?"
 * Rebuilt to the Praxis reference (founder 2026-06-15). The page answers ONE
 * question — "how much can I afford to spend to acquire a customer?" — by
 * showing value by what PREDICTS it (first product, source, discount) across
 * time horizons, then the cohort trend and second-order retention.
 *
 * The global window chooses the measurement horizon. Standard pills use the most
 * recent acquisition cohort that fully completed that horizon. A 90-day view,
 * for example, uses customers acquired 90 to 180 days before the latest synced
 * data and measures those same people through day 90.
 */

import { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { filterClientExplainLines } from "@data-dashboard/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { applyExplainOverride } from "../components/cards.js";
import { CardAnchorCtx } from "../lib/card-anchor.js";
import { useExplainOverride } from "../lib/card-approval.js";
import { useToast } from "../lib/toast.js";
import { useSetupGate, SetupLocked, PullingData, SyncError } from "../lib/setup-gate.js";
import { useDrill } from "../lib/drill.js";
import { usePeriod } from "../lib/period.js";
import { Hideable } from "../lib/hide.js";
import { SortableArea } from "../lib/sortable-cards.js";
import { GatewayProductsCard } from "../cards/gateway-products.js";
import { usePageFilters } from "../lib/page-filters.js";
import { invalidateCatalogQueries, relockFinancialQueries } from "../lib/query-invalidation.js";
import { CohortChart } from "../components/CohortChart.js";
import { ChartTypeMenu, useChartPref } from "../components/ChartTypeMenu.js";
import { CvGroupedBars } from "../components/CvGroupedBars.js";
import { ScatterChart } from "../components/ScatterChart.js";
import { MultiLineCurve } from "../components/MultiLineCurve.js";
import { Segmented } from "../components/Segmented.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "../components/RevenueCmToggle.js";
import { useProductSkus, useProductSkusQuery, useSkuToName, useSkuIdByCode, useSkuDisplayByCode, SkuSub, useListControls, ListSearch, SortTh } from "../components/ListControls.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

/** How the by-first-product tables key their rows. SKU-level is the default. */
export type ProductGrouping = "sku" | "product";

/** Dollar basis for the value cards: net revenue vs contribution margin. */
type ValueMode = "cm" | "gross";

interface Cell { averageLtvBase: number; cohortSize: number }
export interface CohortRow { cohortKey: string; cohortSize: number; cellsByTimeframe: Record<number, Cell> }
export interface FirstProductRow {
  product: string;
  customers: number;
  cvByTimeframe: Record<number, number>;
  /** True all-time cumulative value (founder "total lifetime"). */
  totalLifetime?: number;
  totalLifetimeN?: number;
  /** One fixed mature customer group behind every visible timeframe value. */
  cvCohortByTimeframe?: Record<number, number>;
  growthPct: number | null;
  secondOrderCount: number;
  /** Mature (first order 30+ days ago) population the retention rate runs over —
   *  matches the company-average benchmark so "vs Average" compares like to like. */
  matureCustomers?: number;
  secondOrderCountMature?: number;
  secondOrderRetentionRate: number | null;
}
export interface CvResponse {
  overview: { averageByTimeframe: Array<{ timeframeDays: number; averageLtvBase: number; cohortSize: number }> };
  summary: { aov: number; repeatRate: number | null; repeatCustomers: number; totalCustomers: number };
  curve: Array<{ day: number; avgCv: number; cohortSize: number }>;
  cohort: CohortRow[];
  byFirstProduct: FirstProductRow[];
  /** The basis actually used (CM falls back to gross when the account has no
   *  computed margin) and whether CM is available at all. */
  mode?: ValueMode;
  hasCm?: boolean;
  /** How byFirstProduct is keyed (echoed back from the request). */
  grouping?: ProductGrouping;
  cohortBasis?: { cohortStart: string; cohortEnd: string; horizonDays: number | null; note: string; custom: boolean };
}
export interface DiscountLtvRow { code: string; customers: number; valueCustomers?: number; cv1: number | null; cv30: number; cv60: number | null; cv90: number | null; cv180: number | null; cv365: number | null; totalLifetime?: number | null; n90: number; repeatCustomers?: number; repeatPct: number | null }
export interface AffiliateLtvRow { affiliate: string; customers: number; valueCustomers?: number; cv1: number | null; cv30: number; cv60: number | null; cv90: number | null; cv180: number | null; cv365: number | null; totalLifetime?: number | null; n90: number; repeatCustomers?: number; repeatPct: number | null }

export type CohortChartKind = "bar" | "line" | "heat" | "table";

/** Timeframe series for the COHORT CHART (matches the Praxis legend:
 *  1 Day / 30 / 60 / 90 Days / 6 Months). The value TABLES use ALL_TFS, capped
 *  by the selected window — see tfsForWindow. */
const COHORT_TFS = [1, 30, 60, 90, 180];
/** Every horizon the value tables CAN show (adds 12 months). Which actually
 *  render is driven by the timeline: the window sets HOW FAR OUT to measure
 *  (founder 2026-06-25), so 1 year shows out to Month 12. Within a breakdown
 *  row, every visible value uses the same mature customer group. */
const ALL_TFS = [1, 30, 60, 90, 180, 365];
function tfsForWindow(windowDays: number | null): number[] {
  if (windowDays === null) return ALL_TFS; // all-time
  return ALL_TFS.filter((d) => d <= Math.max(30, windowDays));
}
/** Header label for a horizon column. */
const tfLabel = (d: number): string => (d === 1 ? "1 day" : d === 180 ? "6 mo" : d === 365 ? "12 mo" : `${d}d`);
const money = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const money2 = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

export function BuyGoodsLtvUnavailable({ detailed = true }: { detailed?: boolean }): React.ReactElement {
  return (
    <div>
      <div style={{ color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Customer LTV</div>
      <h1 style={{ margin: "7px 0 16px", fontSize: 27 }}>Customer LTV is unavailable</h1>
      <Card>
        <CardHead title="The source history is not complete enough" />
        <div role="status" style={{ marginTop: 12, color: "var(--body)", fontSize: 13.5, lineHeight: 1.55 }}>
          {detailed
            ? "This account has incomplete or mixed BuyGoods history, so we cannot reliably connect every customer's orders and refunds over time. Customer counts and order history stay available elsewhere, but LTV stays hidden."
            : "This BuyGoods history does not identify customers, products, and refunds well enough to connect each customer's orders over time. Customer counts and order history stay available elsewhere, but LTV stays hidden."}
        </div>
      </Card>
    </div>
  );
}

export function customerLtvReadiness(caps: Capabilities | undefined): {
  grossReady: boolean;
  fullReady: boolean;
  grossOnly: boolean;
} {
  const grossReady = caps?.grossCustomerValue === true;
  const fullReady = caps?.customerValue === true;
  return { grossReady, fullReady, grossOnly: grossReady && !fullReady };
}

export default function CohortsPage({ initialGrouping = "product" }: { initialGrouping?: ProductGrouping } = {}): React.ReactElement {
  const api = useApiClient();
  const period = usePeriod();
  const gate = useSetupGate();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  // Match every Revenue / CM card: start on Revenue, then remember this card's
  // choice between visits. CM changes only the dollar basis, never the people.
  const [basisMode, setBasisMode] = useStickyRevenueCmMode("dd-mode-customer-ltv");
  // Start with the client-facing product view. SKU codes remain one click away
  // for investigation, but should not be the first thing a new client sees.
  // Only this table varies by grouping.
  const [grouping, setGrouping] = useState<ProductGrouping>(initialGrouping);
  const productSkusQ = useProductSkusQuery();
  const capsQ = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Capabilities>("/dashboard/capabilities"),
    enabled: ready,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const { grossReady: grossLtvReady, fullReady: fullLtvReady, grossOnly } = customerLtvReadiness(capsQ.data);
  // Detailed BuyGoods history proves Revenue LTV, but not costs or acquisition
  // attribution. Force the narrow view to gross even if this browser previously
  // saved CM as its preferred basis.
  const valueMode: ValueMode = fullLtvReady && basisMode === "profit" ? "cm" : "gross";

  // The global timeline chooses the horizon. The server selects one fully
  // completed cohort and returns the exact customer-group note with the numbers.
  const customerValueQ = useQuery({
    queryKey: ["customer-value", period.cacheKey, valueMode, grouping],
    queryFn: () => api.get<CvResponse>("/dashboard/customer-value", { ...period.query, count: 120, mode: valueMode, grouping }),
    enabled: ready && grossLtvReady,
  });
  const data = customerValueQ.data;
  // LTV by source / discount / affiliate are ALL period-scoped (they track the
  // window; source also tracks the Revenue/CM toggle) — the acquisition-cohort
  // filter is applied server-side (sub-retention.ts HAVING clause).
  const ltvBySource = useQuery({ queryKey: ["ltv-by-source", period.cacheKey, valueMode], queryFn: () => api.get<{ rows: LtvSourceCurve[] }>("/dashboard/ltv-by-source", { ...period.query, mode: valueMode }), enabled: ready && fullLtvReady });
  const ltvByCode = useQuery({ queryKey: ["ltv-by-discount", period.cacheKey, valueMode], queryFn: () => api.get<{ rows: DiscountLtvRow[] }>("/dashboard/ltv-by-discount", { ...period.query, mode: valueMode }), enabled: ready && fullLtvReady });
  const ltvByAffiliate = useQuery({ queryKey: ["ltv-by-affiliate", period.cacheKey, valueMode], queryFn: () => api.get<{ rows: AffiliateLtvRow[] }>("/dashboard/ltv-by-affiliate", { ...period.query, mode: valueMode }), enabled: ready && fullLtvReady });
  // The timeline sets how far OUT the value tables measure (which horizon columns
  // show) — 1 year → out to Month 12 (founder 2026-06-25).
  const tfs = tfsForWindow(period.windowDays);
  const pf = usePageFilters();
  // A product/source filter that matches NO first-order cohort (e.g. an add-on
  // that's never a customer's first purchase) → every chart is legitimately
  // empty. Say so plainly instead of leaving a wall of "no data" that reads as
  // broken (founder 2026-06-30: biotin on the LTV page).
  const emptyFromFilter = !!data && data.cohort.length === 0 && pf.filterActive;
  const gateLoad = pageLoadState(gate);
  const requiredLoad = pageLoadState(
    capsQ,
    customerValueQ,
    ltvBySource,
    ltvByCode,
    ltvByAffiliate,
  );
  const productSkusLoad = pageLoadState(productSkusQ);

  if (gateLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your customer LTV numbers"
        retrying={gateLoad.retrying}
        onRetry={gateLoad.retry}
      />
    );
  }
  if (gate.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (gate.data?.locked) return <SetupLocked />;
  if (gate.data?.syncStatus === "error") return <SyncError message={gate.data.errorMessage} />;
  if (gate.data?.syncStatus === "pending") return <PullingData />;
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your customer LTV numbers"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  if (grouping === "sku" && productSkusLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your product list"
        retrying={productSkusLoad.retrying}
        onRetry={productSkusLoad.retry}
      />
    );
  }
  if (grouping === "sku" && productSkusQ.data === undefined) {
    return <p role="status" style={{ color: "var(--body)" }}>Loading product names…</p>;
  }
  if (capsQ.isLoading) return <p style={{ color: "var(--body)" }}>Checking source history…</p>;
  if (!grossLtvReady) return <BuyGoodsLtvUnavailable detailed={capsQ.data?.buyGoodsDetailedHistoryGap === true} />;

  // Longest server-approved marker = the most complete LTV we can show for this
  // cohort. The server already chooses one fixed, mature population, so the UI
  // must not impose a second 20-customer gate that hides valid small-account data.
  // A completed 30-day cohort is a real LTV read; only a group that cannot reach
  // day 30 gets the not-enough-history notice.
  const maxVisibleTf = tfs[tfs.length - 1] ?? 365;
  const matureMarkers = data
    ? data.overview.averageByTimeframe.filter((t) => t.cohortSize > 0 && t.timeframeDays <= maxVisibleTf)
    : [];
  const headlineMarker = matureMarkers.length ? matureMarkers[matureMarkers.length - 1] : undefined;
  const insufficientHistory = !!data && (!headlineMarker || headlineMarker.timeframeDays < 30);
  // CM is unavailable when the account has no costs entered → backend falls back
  // to gross. shownMode = the basis actually used; cmAvailable gates the toggle.
  const cmAvailable = fullLtvReady && data?.hasCm !== false;
  const shownMode: ValueMode = data?.mode ?? valueMode;
  return (
    <div>
      <div style={{ color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Customer LTV</div>
      <h1 style={{ margin: "7px 0 0", fontSize: 27 }}>How do we increase our customer LTV over time?</h1>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
        {headlineMarker && !insufficientHistory && (() => {
          // The headline uses the FURTHEST marker enough customers have matured to.
          // On a 90-day window whose 90-day cohort is still too small, that's the
          // 60-day marker — honest, but confusing without a word on WHY (founder
          // 2026-07-06: "$62.92 at 60 days — I have 90 days selected").
          const maxTf = tfs[tfs.length - 1] ?? headlineMarker.timeframeDays;
          const capped = headlineMarker.timeframeDays < maxTf;
          return (
            <span className="dd-num" style={{ fontSize: 16, color: "var(--body)" }}>
              <b style={{ color: "var(--num)", fontSize: 20 }}>{money2(headlineMarker.averageLtvBase)}</b> avg {shownMode === "cm" ? "CM" : "revenue"} per customer at {headlineMarker.timeframeDays} days
              <span style={{ fontSize: 12, color: "var(--faint)" }}> · {headlineMarker.cohortSize.toLocaleString()} customers{headlineMarker.cohortSize < 20 ? "; directional until the group grows" : ""}</span>
              {capped && <span style={{ fontSize: 12, color: "var(--faint)" }}> · the furthest reliable point this group has reached</span>}
            </span>
          );
        })()}
      </div>
      {shownMode === "cm" ? (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--faint)" }}>
          Showing contribution margin (CM): Revenue minus the variable costs to make, ship, and process each order, minus refunds. Switch to Revenue for the top line.
        </div>
      ) : null}

      {insufficientHistory && (
        <div style={{ marginTop: 12, fontSize: 13, color: "var(--body)", background: "color-mix(in srgb, #d97706 11%, transparent)", border: "1px solid color-mix(in srgb, #d97706 40%, var(--border))", borderRadius: "var(--r-md)", padding: "11px 14px", lineHeight: 1.5 }}>
          <b>There is not enough completed history for a 30-day customer value yet.</b> The selected acquisition group does not contain customers with a full 30-day runway. Choose older acquisition dates or widen the range.
        </div>
      )}

      {emptyFromFilter && (
        <div style={{ marginTop: 12, fontSize: 13, color: "var(--body)", background: "color-mix(in srgb, #d97706 11%, transparent)", border: "1px solid color-mix(in srgb, #d97706 40%, var(--border))", borderRadius: "var(--r-md)", padding: "11px 14px", lineHeight: 1.5 }}>
          <b>No customers started with the product(s) you've filtered to.</b> This page groups people by the <b>first thing they ever bought</b> — and the selected product(s) were only ever bought as an add-on or a later repeat order, never someone's first purchase. That's why the charts are empty. Clear the product filter in the sidebar, or pick a product customers buy first, to see its lifetime value.
        </div>
      )}

      {customerValueQ.isLoading && <p style={{ marginTop: 20 }}>Loading your numbers…</p>}

      {data && (
        <div style={{ marginTop: 22 }}>
        <SortableArea pageKey="cohorts">
          {/* "How your customers pay back" (payback curve) + "AVG LTV by cohort"
              cards removed (founder 2026-07-07): the breakdown table below covers
              it, and both live inside each row's drill anyway. */}
          <Hideable id="cv-breakdown" label="LTV by product / source / discount / affiliate" span={12}>
            <LtvBreakdownCard
              productRows={data.byFirstProduct}
              sourceRows={ltvBySource.data?.rows}
              discountRows={ltvByCode.data?.rows}
              affiliateRows={ltvByAffiliate.data?.rows}
              tfs={tfs}
              cohortNote={data.cohortBasis?.note}
              grouping={grouping}
              onGrouping={setGrouping}
              basisToggle={cmAvailable ? <RevenueCmToggle
                mode={shownMode === "cm" ? "profit" : "revenue"}
                setMode={setBasisMode}
              /> : null}
              grossOnly={grossOnly}
            />
          </Hideable>

          <Hideable id="cv-gateway" label="Which products create your best customers?" span={12}><GatewayProductsCard revenueOnly={!cmAvailable} /></Hideable>
        </SortableArea>
        </div>
      )}
    </div>
  );
}

/* ---------- exported card sections (also used by cards/cohorts.tsx previews) ---------- */

/** Normalized row for the shared LTV value table: a label, the cohort size, the
 *  value at each horizon, and (optionally) the matured-customer count per
 *  horizon. Every Customer-LTV breakdown maps onto this shape. */
interface LtvTableRow {
  /** Stable filter/action key when the readable label is different (for SKU rows). */
  key?: string;
  label: string;
  customers: number;
  cv: Record<number, number | null | undefined>;
  n?: Record<number, number | undefined>;
  /** Every known order for the same fixed mature group, not a prediction. */
  total?: number | null;
  totalN?: number;
}

/** "Group these N SKUs into a product" — shown under the search bar in SKU view.
 *  Name it → create; then a one-tap Undo (founder 2026-06-26: "search a product
 *  name, group them, saves everywhere"). The card owns the actual save via onGroup. */
function GroupSkusControl({ count, labels, defaultName, onGroup }: {
  count: number;
  labels: string[];
  defaultName: string;
  onGroup: (name: string, labels: string[]) => Promise<{ undo: () => Promise<void> } | void>;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ name: string; count: number; undo: () => Promise<void> } | null>(null);
  const pill: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, border: "1px solid var(--border)", borderRadius: 8, padding: "6px 11px", cursor: "pointer", background: "var(--surface-2)", color: "var(--text)" };
  if (done) {
    return (
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "var(--good)", background: "color-mix(in srgb, var(--good) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--good) 35%, var(--border))", borderRadius: 8, padding: "7px 11px" }}>
        <span>✓ Grouped {done.count} SKUs into “{done.name}”. Switch to <b>Products</b> to see it.</span>
        <button style={{ ...pill, marginLeft: "auto", padding: "3px 9px" }} onClick={() => { void done.undo(); setDone(null); }}>Undo</button>
      </div>
    );
  }
  if (!open) {
    return (
      <div style={{ marginTop: 8 }}>
        <button style={{ ...pill, borderColor: "color-mix(in srgb, var(--accent) 45%, var(--border))", color: "var(--accent)" }}
          onClick={() => { setOpen(true); setName(defaultName.charAt(0).toUpperCase() + defaultName.slice(1)); }}>
          ⊕ Group these {count} SKUs into a product
        </button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name"
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
        style={{ fontSize: 13, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)", color: "var(--text)", minWidth: 220 }} />
      <button disabled={busy || !name.trim()} style={{ ...pill, opacity: busy || !name.trim() ? 0.5 : 1, background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" }}
        onClick={async () => {
          setBusy(true);
          const res = await onGroup(name.trim(), labels);
          setBusy(false); setOpen(false);
          if (res && res.undo) setDone({ name: name.trim(), count: labels.length, undo: res.undo });
        }}>{busy ? "Grouping…" : `Create group (${count})`}</button>
      <button style={pill} onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

/** The shared "LTV by X" table (founder 2026-06-25 prefers this over the grouped
 *  bars). Columns = the horizons for the selected window; Increase % = the value
 *  at the FURTHEST shown column vs the customer's Day-1 first-order value
 *  ("Day 1 → whatever timeframe you're looking at"). Each row drills via onPick. */
function CvLtvTable({ title, explain, labelHeader, rows, tfs, onPick, customersTip, skus, searchPlaceholder, headerExtra, subLabel, chartKey, charts, onGroup, cohortNote }: {
  title: string;
  explain: string[];
  labelHeader: string;
  rows: LtvTableRow[] | undefined;
  tfs: number[];
  onPick?: (key: string, label?: string) => void;
  customersTip?: string;
  skus?: Record<string, string[]>;
  searchPlaceholder?: string;
  headerExtra?: React.ReactNode;
  subLabel?: (label: string) => string | undefined;
  /** Optional chart views shown via a chart-type dropdown; "Table" is always
   *  appended. When set, the card defaults to the first chart (founder 2026-06-26:
   *  "mix it up" — these breakdowns shouldn't all be the same table). */
  chartKey?: string;
  charts?: Array<{ value: string; label: string; node: React.ReactNode }>;
  /** When set, a "Group these N SKUs into a product" control appears while a search
   *  is active and matches ≥2 rows — combine SKUs from the card itself (founder
   *  2026-06-26: "search a product name, group them, saves everywhere"). Returns an
   *  optional undo handler. */
  onGroup?: (name: string, labels: string[]) => Promise<{ undo: () => Promise<void> } | void>;
  cohortNote?: string;
}): React.ReactElement {
  const rowKey = (r: LtvTableRow): string => r.key ?? r.label;
  // Increase % = furthest shown horizon that HAS data ÷ the Day-1 value − 1.
  const increase = (r: LtvTableRow): { pct: number; at: number } | null => {
    const base = r.cv[1];
    if (!base || base <= 0) return null;
    for (let i = tfs.length - 1; i >= 0; i--) {
      const d = tfs[i];
      if (d === undefined || d === 1) continue;
      const v = r.cv[d];
      if (v !== null && v !== undefined) return { pct: v / base - 1, at: d };
    }
    return null;
  };
  const ctrl = useListControls(rows, {
    // Search matches the row label, its muted second line (the product name in SKU
    // view), AND its SKU codes (in Products view) — so typing a product name finds
    // its SKUs even when the SKU tab is active, and vice versa (founder 2026-06-26).
    searchText: (r) => `${r.label} ${rowKey(r)} ${subLabel?.(rowKey(r)) ?? ""} ${(skus?.[rowKey(r)] ?? []).join(" ")}`,
    sortVal: (r, key) =>
      key === "label" ? r.label : key === "customers" ? r.customers : key === "increase" ? (increase(r)?.pct ?? null) : key === "total" ? (r.total ?? null) : key.startsWith("tf:") ? r.cv[Number(key.slice(3))] : null,
    defaultSort: { key: "customers", dir: "desc" },
  });
  // Chart-type dropdown: the supplied chart views + a Table view (founder's choice
  // persists per card). No charts → always the table (the product hero stays a table).
  const pref = useChartPref(chartKey, charts?.[0]?.value ?? "table");
  const view = charts ? pref.type : "table";
  const activeChart = charts?.find((c) => c.value === view)?.node;
  const colSpan = tfs.length + 4;
  // Minimum-customers filter — hides tiny-sample rows (e.g. a 1-customer row
  // reading +3,491%) that skew the read and the average (founder 2026-07-01).
  const [minCust, setMinCust] = useState(1);
  const shown = ctrl.visible.filter((r) => r.customers >= minCust);
  // Weighted average across the shown rows (the footer). Each row's value is
  // weighted by the customers who reached that marker, so a big row counts more
  // than a tiny one — the honest overall average.
  const avgRow = (() => {
    if (shown.length === 0) return null;
    const totCust = shown.reduce((a, r) => a + r.customers, 0);
    const avg: Record<number, number | null> = {};
    for (const d of tfs) {
      let num = 0, den = 0;
      for (const r of shown) {
        const v = r.cv[d];
        if (v === null || v === undefined) continue;
        const w = r.n?.[d] ?? r.customers;
        num += v * w; den += w;
      }
      avg[d] = den > 0 ? num / den : null;
    }
    const base = avg[1];
    let last: number | null = null;
    for (let i = tfs.length - 1; i >= 0; i--) { const d = tfs[i]; if (d === undefined || d === 1) continue; const value = avg[d]; if (value !== null && value !== undefined) { last = value; break; } }
    const inc = base && base > 0 && last !== null ? last / base - 1 : null;
    // Total-lifetime weighted average uses the people behind that value, not the
    // broader Customers column. This preserves population parity in the footer.
    let tnum = 0, tden = 0;
    for (const r of shown) {
      if (r.total === null || r.total === undefined) continue;
      const w = r.totalN ?? r.n?.[tfs[tfs.length - 1] ?? 365] ?? r.customers;
      tnum += r.total * w;
      tden += w;
    }
    const avgTotal = tden > 0 ? tnum / tden : null;
    return { totCust, avg, inc, avgTotal };
  })();
  return (
    <Card>
      <CardHead title={title} explain={explain} />
      {cohortNote && (
        <div style={{ marginTop: 7, fontSize: 11.5, lineHeight: 1.45, color: "var(--body)", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 7, padding: "6px 9px" }}>
          <b>Customer group:</b> {cohortNote}.
        </div>
      )}
      {(headerExtra || charts) && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
          {headerExtra}
          {charts && (
            <ChartTypeMenu
              value={view}
              options={[...charts.map((c) => ({ value: c.value, label: c.label })), { value: "table", label: "Table" }]}
              onChange={pref.setType}
            />
          )}
        </div>
      )}
      {view !== "table" && activeChart ? (
        <div style={{ marginTop: 14 }}>{activeChart}</div>
      ) : (
      <>
      {rows !== undefined && ctrl.total > 8 && (
        <ListSearch q={ctrl.q} setQ={ctrl.setQ} placeholder={searchPlaceholder ?? `Search ${labelHeader.toLowerCase()}…`} matched={ctrl.matched} total={ctrl.total} />
      )}
      {onGroup && ctrl.q.trim() !== "" && ctrl.matched >= 2 && (
        <GroupSkusControl key={ctrl.q} count={ctrl.matched} labels={ctrl.visible.map(rowKey)} defaultName={ctrl.q.trim()} onGroup={onGroup} />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
        {onPick ? <span style={{ fontSize: 13, fontWeight: 600, color: "var(--accent)" }}>Click any row for its full lifetime-value curve →</span> : <span />}
        <label style={{ fontSize: 11.5, color: "var(--body)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          Min customers
          <input type="number" min={1} value={minCust} onChange={(e) => setMinCust(Math.max(1, Number(e.target.value) || 1))}
            title="Hide rows with fewer than this many customers (keeps tiny-sample outliers from skewing the average)"
            style={{ width: 58, padding: "3px 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface-2)", color: "var(--text)", fontSize: 12 }} />
        </label>
      </div>
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 460, marginTop: 10 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <SortTh label={labelHeader} sortKey="label" sort={ctrl.sort} onSort={ctrl.toggleSort} numeric={false} style={th("left")} />
              <SortTh label="Customers" sortKey="customers" sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />
              {tfs.map((d) => <SortTh key={d} label={tfLabel(d)} sortKey={`tf:${d}`} sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />)}
              <SortTh label="Total lifetime" sortKey="total" sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />
              <SortTh label="Increase %" sortKey="increase" sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />
            </tr>
          </thead>
          <tbody>
            {rows === undefined && <tr><td colSpan={colSpan} style={{ ...td(), color: "var(--faint)" }}>Loading…</td></tr>}
            {shown.map((r) => {
              const inc = increase(r);
              const secondaryLabel = subLabel?.(rowKey(r));
              return (
                <tr key={rowKey(r)} onClick={onPick ? () => onPick(rowKey(r), r.label) : undefined} className={onPick ? "dd-drill-row" : undefined} style={{ cursor: onPick ? "pointer" : "default" }}>
                  <td style={{ ...td(), textAlign: "left", color: "var(--text)", fontWeight: 600, maxWidth: 280 }} title={`${r.label}${secondaryLabel ? ` · ${secondaryLabel}` : ""}`}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</div>
                    {secondaryLabel && <div style={{ fontSize: 11, color: "var(--faint)", fontWeight: 400, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{secondaryLabel}</div>}
                    {skus && <SkuSub skus={skus[rowKey(r)]} cap={6} />}
                  </td>
                  <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num" title={customersTip}>{r.customers.toLocaleString()}</td>
                  {tfs.map((d) => {
                    const n = r.n?.[d];
                    return (
                      <td key={d} style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">
                        {money(r.cv[d])}
                        {n !== undefined && n > 0 && <div style={{ fontSize: 10.5, color: "var(--faint)", fontWeight: 400 }}>{n.toLocaleString()} customers</div>}
                      </td>
                    );
                  })}
                  <td style={{ ...td(), textAlign: "right", color: "var(--num)", fontWeight: 700 }} className="dd-num" title="Every known order to date for the same mature customer group. This is observed value, not a prediction.">
                    {r.total === null || r.total === undefined ? <span style={{ color: "var(--faint)" }}>—</span> : (
                      <>{money(r.total)}{r.totalN !== undefined && r.totalN > 0 && <div style={{ fontSize: 10.5, color: "var(--faint)", fontWeight: 400 }}>{r.totalN.toLocaleString()} customers</div>}</>
                    )}
                  </td>
                  <td style={{ ...td(), textAlign: "right" }}>
                    {inc === null ? <span style={{ color: "var(--faint)" }}>—</span> : (
                      <span className="dd-num" title={`Day 1 → ${tfLabel(inc.at)}`} style={{ fontWeight: 700, color: inc.pct >= 0 ? "var(--good)" : "var(--bad)" }}>{inc.pct >= 0 ? "+" : ""}{Math.round(inc.pct * 100)}%</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows?.length === 0 && <tr><td colSpan={colSpan} style={{ ...td(), color: "var(--faint)" }}>No data yet.</td></tr>}
            {rows !== undefined && ctrl.matched === 0 && ctrl.q !== "" && <tr><td colSpan={colSpan} style={{ ...td(), color: "var(--faint)" }}>No matches for “{ctrl.q}”.</td></tr>}
            {rows !== undefined && rows.length > 0 && ctrl.matched > 0 && shown.length === 0 && <tr><td colSpan={colSpan} style={{ ...td(), color: "var(--faint)" }}>No rows with at least {minCust} customers — lower the minimum.</td></tr>}
          </tbody>
          {avgRow && shown.length > 0 && (
            <tfoot>
              <tr style={{ borderTop: "2px solid var(--border)" }}>
                <td style={{ ...td(), textAlign: "left", color: "var(--text)", fontWeight: 700 }}>Average (shown)</td>
                <td style={{ ...td(), textAlign: "right", color: "var(--num)", fontWeight: 700 }} className="dd-num">{avgRow.totCust.toLocaleString()}</td>
                {tfs.map((d) => <td key={d} style={{ ...td(), textAlign: "right", color: "var(--num)", fontWeight: 700 }} className="dd-num">{money(avgRow.avg[d])}</td>)}
                <td style={{ ...td(), textAlign: "right", color: "var(--num)", fontWeight: 700 }} className="dd-num">{avgRow.avgTotal === null || avgRow.avgTotal === undefined ? <span style={{ color: "var(--faint)" }}>—</span> : money(avgRow.avgTotal)}</td>
                <td style={{ ...td(), textAlign: "right" }}>
                  {avgRow.inc === null ? <span style={{ color: "var(--faint)" }}>—</span> : (
                    <span className="dd-num" style={{ fontWeight: 700, color: avgRow.inc >= 0 ? "var(--good)" : "var(--bad)" }}>{avgRow.inc >= 0 ? "+" : ""}{Math.round(avgRow.inc * 100)}%</span>
                  )}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      </>
      )}
    </Card>
  );
}

/** "AVG Customer Value by First Order Products" — the headline answer table.
 *  Defaults to one row per SKU (founder 2026-06-26: "separate to start, group only
 *  when you want"); the toggle rolls SKUs into the founder's product families. */
export function CvByFirstProductTable({ rows, tfs = ALL_TFS, grouping, onGrouping, breakdownToggle, basisToggle, cohortNote, drillEnabled = true, revenueOnly = false }: {
  rows: FirstProductRow[];
  tfs?: number[];
  grouping: ProductGrouping;
  onGrouping: (g: ProductGrouping) => void;
  /** The combined card's "Break down by" toggle, shown left of the SKUs/Products toggle. */
  breakdownToggle?: React.ReactNode;
  basisToggle?: React.ReactNode;
  cohortNote?: string;
  drillEnabled?: boolean;
  revenueOnly?: boolean;
}): React.ReactElement {
  const drill = useDrill();
  const skus = useProductSkus();
  const skuToName = useSkuToName();
  const skuDisplayByCode = useSkuDisplayByCode();
  const skuIdByCode = useSkuIdByCode();
  const api = useApiClient();
  const toast = useToast();
  const qc = useQueryClient();
  const sku = grouping === "sku";
  const skuSubLabel = (code: string): string => {
    const display = skuDisplayByCode[code];
    return display
      ? [display.details, display.code].filter(Boolean).join(" · ")
      : `SKU ${code}`;
  };
  // In SKU view, let the founder combine the searched SKUs into one product — the
  // same write onboarding uses (create group + relocate). Grouping touches the
  // overlay that EVERY "by product" surface reads, so we invalidate the whole query
  // cache: that's what makes it "save everywhere" (Codex). Heavy but rare (an
  // intentional click).
  const invalidate = (): Promise<unknown> => invalidateCatalogQueries(qc);
  const onGroup = sku
    ? async (name: string, codes: string[]): Promise<{ undo: () => Promise<void> } | void> => {
        const skuIds = codes.map((c) => skuIdByCode[c]).filter(Boolean);
        if (skuIds.length < 2) { toast.error("Couldn't match those SKUs — give the catalog a second and retry."); return; }
        let groupId: string | null = null;
        try {
          const { group } = await api.post<{ group: { id: string } }>("/catalog/groups", { name });
          groupId = group.id;
          await api.patch("/catalog/classifications", { edits: skuIds.map((skuId) => ({ skuId, productGroupId: group.id, variantLabel: null, status: "confirmed" })) });
          relockFinancialQueries(qc);
          await invalidate();
          toast.success(`Grouped ${skuIds.length} SKUs into “${name}”.`);
          return {
            // V1 Undo: delete the new group (its SKUs return to review/ungrouped). It
            // does NOT restore a prior group a SKU may have had — refine in onboarding.
            undo: async (): Promise<void> => {
              try { await api.delete(`/catalog/groups/${group.id}`); relockFinancialQueries(qc); await invalidate(); toast.success("Ungrouped."); }
              catch { toast.error("Couldn't undo that — adjust it in onboarding."); }
            },
          };
        } catch {
          // If create succeeded but classify failed, don't leave an empty orphan group.
          if (groupId) {
            try { await api.delete(`/catalog/groups/${groupId}`); } catch { /* best effort */ }
            relockFinancialQueries(qc);
          }
          toast.error("Couldn't create that group.");
        }
      }
    : undefined;
  return (
    <CvLtvTable
      title="Average customer LTV by first-order product"
      labelHeader={sku ? "First-order SKU" : "First-order product"}
      onGroup={onGroup}
      tfs={tfs}
      cohortNote={cohortNote}
      // SKU view: the readable offer leads; the raw code remains the stable key
      // and appears on the muted second line.
      // Product view: each row is a family — show its SKU codes (capped).
      skus={sku ? undefined : skus}
      subLabel={sku ? skuSubLabel : undefined}
      headerExtra={
        <>
          {breakdownToggle}
          {basisToggle}
          <Segmented
            ariaLabel="Group rows by"
            size="sm"
            value={grouping}
            onChange={onGrouping}
            options={[{ value: "sku", label: "SKUs" }, { value: "product", label: "Products" }]}
          />
        </>
      }
      searchPlaceholder="Search — combine terms, e.g. b1g1 + biotin"
      explain={[
        sku
          ? "<b>What it is:</b> Groups customers by the SKU they bought first, then shows what they're worth on average at 1 / 30 / 60 / 90 / 180 / 365 days after joining — which front-door product brings the most valuable customers over time. The free bonus and post-checkout upsells don't count; a customer whose first order had two SKUs is counted in both rows, so rows don't sum to the total."
          : "<b>What it is:</b> Groups customers by the product family they bought first, then shows what they're worth on average at 1 / 30 / 60 / 90 / 180 / 365 days after joining — which front-door product brings the most valuable customers over time. Toggle to SKUs to split each one out. The free bonus and post-checkout upsells don't count; a customer whose first order had two products is counted in both rows, so rows don't sum to the total.",
        "<b>How it's calculated:</b> First, find the furthest reliable day this row can reach. Then use the customers who have reached that day for every value from Day 1 through that last day. The small n under each dollar is that fixed customer count. Increase % compares Day 1 with the last visible day for those same people.",
        "<b>Example:</b> If 407 customers have reached Day 90, Day 1, Day 30, Day 60, and Day 90 all use those same 407 people. The customer group never changes underneath the line.",
        revenueOnly
          ? "<b>Note:</b> Standard date choices use the most recent customer group that fully completed the selected horizon. A 90-day view uses customers acquired 90 to 180 days before the latest sync. Every visible day uses the same people. Total lifetime adds every known order for that group and is not a prediction. Dollars are Gross LTV before costs."
          : "<b>Note:</b> Standard date choices use the most recent customer group that fully completed the selected horizon. A 90-day view uses customers acquired 90 to 180 days before the latest sync. Every visible day uses the same people. Total lifetime adds every known order for that group and is not a prediction. Revenue / CM changes only the dollar basis.",
      ]}
      rows={rows.map((r) => ({
        key: r.product,
        label: sku ? skuDisplayByCode[r.product]?.description ?? skuToName[r.product] ?? `SKU ${r.product}` : r.product,
        customers: r.customers,
        cv: r.cvByTimeframe,
        n: r.cvCohortByTimeframe,
        total: r.totalLifetime,
        totalN: r.totalLifetimeN,
      }))}
      onPick={drillEnabled ? (p, label) => drill.open({ title: label ?? p, metric: "new_customers", ltv: { product: p } }) : undefined}
      customersTip="Everyone who started on this SKU or product. The fixed mature group behind the dollar values is shown as n."
    />
  );
}

/** "AVG CV by Source" — the value table (founder 2026-06-25: table, not bars). */
/** Per-source LTV curve from /dashboard/ltv-by-source (period + mode scoped). */
export interface LtvSourceCurve { source: string; customers: number; points: Array<{ day: number; avgCv: number; n: number }>; totalLifetime?: number; totalLifetimeN?: number }

export function CvBySourceCard({ rows: srcRows, tfs = ALL_TFS, breakdownToggle, basisToggle, cohortNote }: { rows: LtvSourceCurve[] | undefined; tfs?: number[]; breakdownToggle?: React.ReactNode; basisToggle?: React.ReactNode; cohortNote?: string }): React.ReactElement {
  const drill = useDrill();
  const onPick = (src: string): void => drill.open({ title: src, metric: "customers", ltv: { channel: src } });
  // Read a source's value / matured-count at a given day from its curve points. A
  // missing day = cohort not mature yet → null (table shows "—", curve breaks).
  const at = (r: LtvSourceCurve, day: number): number | null => r.points.find((p) => p.day === day)?.avgCv ?? null;
  const nAt = (r: LtvSourceCurve, day: number): number | undefined => r.points.find((p) => p.day === day)?.n;
  const has = srcRows !== undefined && srcRows.length > 0;
  // The north-star view (founder): a day-by-day CURVE of how each source's customer
  // value BUILDS over time — one line per source, 9 points, x spaced by real days so
  // the fast-early / flattening shape shows. Grouped bars + Table are alternatives.
  const curveDays = [1, 30, 60, 90, 120, 180, 240, 300, 365];
  const curve = has && (
    <MultiLineCurve days={curveDays} onPick={onPick}
      series={srcRows.map((r) => ({ name: r.source, values: curveDays.map((d) => at(r, d)) }))} />
  );
  const bars = has && (
    <CvGroupedBars max={10} onPick={onPick}
      rows={srcRows.map((r) => ({ label: r.source, cv1: at(r, 1), cv30: at(r, 30), cv60: at(r, 60), cv90: at(r, 90), cv180: at(r, 180), cv365: at(r, 365), customers: r.customers }))} />
  );
  const rows = srcRows?.map((r) => ({ label: r.source, customers: r.customers, cv: { 1: at(r, 1), 30: at(r, 30), 60: at(r, 60), 90: at(r, 90), 180: at(r, 180), 365: at(r, 365) }, n: { 1: nAt(r, 1), 30: nAt(r, 30), 60: nAt(r, 60), 90: nAt(r, 90), 180: nAt(r, 180), 365: nAt(r, 365) }, total: r.totalLifetime, totalN: r.totalLifetimeN }));
  return (
    <CvLtvTable
      title="Average customer LTV by source"
      labelHeader="Source"
      tfs={tfs}
      cohortNote={cohortNote}
      headerExtra={<>{breakdownToggle}{basisToggle}</>}
      chartKey="cv-by-source"
      charts={curve && bars ? [{ value: "curve", label: "Curve", node: curve }, { value: "bars", label: "Grouped bars", node: bars }] : undefined}
      explain={[
        "<b>What it is:</b> The same value-over-time curve, split by the marketing source of each customer's first order — which channel brings customers worth the most as they age. Each line is one source; the higher and steeper it climbs, the better those customers.",
        "<b>How it's calculated:</b> Each customer is tagged by the source of their first order. Blank sources show as Unknown. For each source, we find its furthest reliable day and use the same mature customers for every point leading to it.",
        "<b>Example:</b> If a source has 500 selected customers and 220 have reached its last reliable day, Customers shows 500 and every value on that line uses the same n=220.",
        "<b>Note:</b> Source is first touch only. Standard date choices use the most recent customer group that fully completed the selected horizon. Every point uses the same people. Total lifetime is every known order for that group, not a prediction. Dollars are rounded to whole numbers.",
      ]}
      rows={rows}
      onPick={onPick}
    />
  );
}

/** "AVG CV by Discount" — the value table. */
export function CvByDiscountCard({ rows: discountRows, tfs = ALL_TFS, breakdownToggle, basisToggle, cohortNote }: { rows: DiscountLtvRow[] | undefined; tfs?: number[]; breakdownToggle?: React.ReactNode; basisToggle?: React.ReactNode; cohortNote?: string }): React.ReactElement {
  const drill = useDrill();
  const rows = discountRows
    ? discountRows
        .map((r) => {
          const n = r.valueCustomers ?? r.n90;
          const cv = { 1: r.cv1, 30: r.cv30, 60: r.cv60, 90: r.cv90, 180: r.cv180, 365: r.cv365 };
          return { label: r.code, customers: r.customers, cv, n: Object.fromEntries(Object.entries(cv).map(([day, value]) => [Number(day), value === null || value === undefined ? undefined : n])), total: r.totalLifetime, totalN: n };
        })
    : undefined;
  // Default to a scatter: the real question is a RELATIONSHIP — does a code buy
  // valuable repeat customers or cheap one-and-dones? x = 90-day value, y = repeat
  // rate, bubble = how many customers (so a tiny code can't masquerade as a winner).
  const scatterRows = discountRows?.filter((r) => r.cv90 !== null && r.repeatPct !== null) ?? [];
  const scatter = scatterRows.length > 0 && (
    <ScatterChart
      xLabel="90-day value / customer"
      yLabel="Repeat rate"
      xMoney
      ySuffix="%"
      points={scatterRows.map((r) => ({ x: r.cv90!, y: r.repeatPct!, size: r.customers, label: r.code }))}
    />
  );
  return (
    <CvLtvTable
      title="Average customer LTV by discount code"
      labelHeader="Discount code"
      tfs={tfs}
      cohortNote={cohortNote}
      headerExtra={<>{breakdownToggle}{basisToggle}</>}
      chartKey="cv-by-discount"
      charts={scatter ? [{ value: "scatter", label: "Value vs repeat", node: scatter }] : undefined}
      explain={[
        "<b>What it is:</b> Average value over time grouped by the discount code on each customer's first order. Do coupon customers end up worth less than full-price buyers? Mature codes also appear as bubbles: 90-day value across, 90-day repeat rate up, and total selected customers shown by bubble size.",
        "<b>How it's calculated:</b> Group customers by the coupon on their first order. Each row uses one fixed mature group from Day 1 through its last reliable day. The repeat rate is separate: customers who had a full 90 days and placed their second order inside those 90 days.",
        "<b>Example:</b> If No code has 1,000 selected customers and 600 have reached its last reliable day, Customers shows 1,000 and every visible dollar uses n=600.",
        "<b>Note:</b> Standard date choices use the most recent customer group that fully completed the selected horizon. Every visible day uses the same people. Total lifetime adds every known order for that group. Revenue / CM changes only the dollar basis. CM turns on after every selling product has costs.",
      ]}
      rows={rows}
      onPick={(code) => drill.open({ title: code, metric: "customers", ltv: { discount: code } })}
    />
  );
}

export function CvByAffiliateCard({ rows: affRows, tfs = ALL_TFS, breakdownToggle, basisToggle, cohortNote }: { rows: AffiliateLtvRow[] | undefined; tfs?: number[]; breakdownToggle?: React.ReactNode; basisToggle?: React.ReactNode; cohortNote?: string }): React.ReactElement {
  const drill = useDrill();
  const rows = affRows
    ? affRows
        .map((r) => {
          const n = r.valueCustomers ?? r.n90;
          const cv = { 1: r.cv1, 30: r.cv30, 60: r.cv60, 90: r.cv90, 180: r.cv180, 365: r.cv365 };
          return { label: r.affiliate, customers: r.customers, cv, n: Object.fromEntries(Object.entries(cv).map(([day, value]) => [Number(day), value === null || value === undefined ? undefined : n])), total: r.totalLifetime, totalN: n };
        })
    : undefined;
  return (
    <CvLtvTable
      title="Average customer LTV by affiliate"
      labelHeader="Affiliate"
      tfs={tfs}
      cohortNote={cohortNote}
      headerExtra={<>{breakdownToggle}{basisToggle}</>}
      explain={[
        "<b>What it is:</b> Average lifetime value grouped by the affiliate that drove each customer's first order — which affiliates send customers actually worth it long-term. Compare each affiliate to the <b>Average (shown)</b> row at the bottom.",
        "<b>How it's calculated:</b> Group customers by the affiliate on their first touch. Each row uses one fixed mature group from Day 1 through its last reliable day. The repeat rate uses customers who had a full 90 days and asks whether they ordered again inside those 90 days.",
        "<b>Example:</b> If an affiliate has 300 selected customers and 140 have reached its last reliable day, Customers shows 300 and every visible dollar uses n=140.",
        "<b>Note:</b> Standard date choices use the most recent customer group that fully completed the selected horizon. Every visible day uses the same people. Total lifetime adds every known order for that group. Revenue / CM changes only the dollar basis. Click an affiliate for its full value curve.",
      ]}
      rows={rows}
      onPick={(aff) => drill.open({ title: aff, metric: "customers", ltv: { affiliate: aff } })}
    />
  );
}

/**
 * Combined "LTV by …" card (founder 2026-07-06: "combine the LTV by first-order
 * product / source / discount / affiliate — a toggle within the card, it'd clean
 * up the page"). ONE card with a "Break down by" segmented toggle that swaps the
 * data source; each breakdown reuses the shared CvLtvTable (incl. the Total lifetime
 * column). First-product keeps its own SKUs/Products sub-toggle + group-to-create.
 */
function LtvBreakdownCard({ productRows, sourceRows, discountRows, affiliateRows, tfs, grouping, onGrouping, basisToggle, cohortNote, grossOnly = false }: {
  productRows: FirstProductRow[];
  sourceRows: LtvSourceCurve[] | undefined;
  discountRows: DiscountLtvRow[] | undefined;
  affiliateRows: AffiliateLtvRow[] | undefined;
  tfs: number[];
  grouping: ProductGrouping;
  onGrouping: (g: ProductGrouping) => void;
  basisToggle: React.ReactNode;
  cohortNote?: string;
  grossOnly?: boolean;
}): React.ReactElement {
  // Deep-link support (founder's blended model 2026-07-13): a "View by channel →"
  // link from the Marketing / cockpit LTV:CAC cards lands here on the Source tab
  // (?tab=source, or ?tab=channel as an alias for the founder's wording). Read once
  // on mount — the link navigates from another page, so the card remounts here.
  const [searchParams] = useSearchParams();
  const initialDim = ((): "product" | "source" | "discount" | "affiliate" => {
    const t = searchParams.get("tab");
    if (t === "source" || t === "channel") return "source";
    if (t === "discount") return "discount";
    if (t === "affiliate") return "affiliate";
    return "product";
  })();
  const [dim, setDim] = useState<"product" | "source" | "discount" | "affiliate">(initialDim);
  const toggle = (
    <Segmented
      ariaLabel="Break down by"
      size="sm"
      value={dim}
      onChange={setDim}
      options={[
        { value: "product", label: "First product" },
        { value: "source", label: "Source" },
        { value: "discount", label: "Discount" },
        { value: "affiliate", label: "Affiliate" },
      ]}
    />
  );
  if (grossOnly) {
    return <CvByFirstProductTable rows={productRows} tfs={tfs} grouping={grouping} onGrouping={onGrouping} cohortNote={cohortNote} drillEnabled={false} revenueOnly />;
  }
  if (dim === "source") return <CvBySourceCard rows={sourceRows} tfs={tfs} breakdownToggle={toggle} basisToggle={basisToggle} cohortNote={cohortNote} />;
  if (dim === "discount") return <CvByDiscountCard rows={discountRows} tfs={tfs} breakdownToggle={toggle} basisToggle={basisToggle} cohortNote={cohortNote} />;
  if (dim === "affiliate") return <CvByAffiliateCard rows={affiliateRows} tfs={tfs} breakdownToggle={toggle} basisToggle={basisToggle} cohortNote={cohortNote} />;
  return <CvByFirstProductTable rows={productRows} tfs={tfs} grouping={grouping} onGrouping={onGrouping} breakdownToggle={toggle} basisToggle={basisToggle} cohortNote={cohortNote} />;
}

/** "AVG Customer Value" cohort chart — value-by-timeframe bars per first-purchase
 *  month + the cohort-count line (catalog id: ltv-cohort-matrix). Clicking the
 *  chart opens the all-customers LTV drill (founder: every card drillable). */
export function CohortMatrixCard({ chrono, chart: cohortChart, onChart }: { chrono: CohortRow[]; chart: CohortChartKind; onChart: (v: CohortChartKind) => void }): React.ReactElement {
  const drill = useDrill();
  const verdict = cohortVerdict(chrono, 90);
  return (
    <Card style={{ marginTop: 16 }} onClick={() => drill.open({ title: "All customers", metric: "new_customers", ltv: {} })} title="Open the LTV drill">
      <CardHead
        title="Average LTV by cohort"
        explain={[
          "<b>What it is:</b> Each slot is the month a set of customers first bought (their sign-up cohort). The bars are their average value at 1 / 30 / 60 / 90 / 180 days as that cohort ages; the line is how many customers joined that month. Are newer cohorts more valuable at the same age?",
          "<b>How it's calculated:</b> Group customers by the month of their first order. At each day marker, average everyone's cumulative CM or Revenue only after the whole cohort has had enough time to reach that marker. A too-recent cell stays blank instead of showing a partial number.",
          "<b>Example (sample data · year-to-date):</b> The January 2026 cohort = 972 customers, worth $53.91 by day 1, $69.58 by day 60, and $76.23 by day 90. The May 2026 cohort's 90-day bar is empty — those customers haven't lived 90 days yet.",
          "<b>Note:</b> Rows are acquisition cohorts; the bars are age since joining, never the calendar window up top. An empty bar means not old enough yet, not missing data — so recent months drop off for the longer windows. Newer months shorter than older = quality slipping.",
        ]}
      >
        <span onClick={(e) => e.stopPropagation()}><Switcher value={cohortChart} onChange={(v) => onChart(v as CohortChartKind)} opts={[["bar", "▮▮"], ["line", "∿"], ["heat", "▦"], ["table", "☰"]]} /></span>
      </CardHead>
      {verdict && (
        <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.5, color: "var(--body)", borderLeft: `3px solid ${verdict.tone === "bad" ? "var(--bad)" : verdict.tone === "good" ? "var(--good)" : "var(--border)"}`, paddingLeft: 10 }}
          dangerouslySetInnerHTML={{ __html: verdict.html }} />
      )}
      <div style={{ marginTop: 12 }}>
        {cohortChart === "bar" || cohortChart === "line"
          ? <CohortChart rows={chrono} timeframes={COHORT_TFS} mode={cohortChart} />
          : <div dangerouslySetInnerHTML={{ __html: cohortTable(chrono, cohortChart === "heat", 90) }} />}
      </div>
    </Card>
  );
}

/** "Retention Rate by First Order Products" — Praxis table + a "vs Average"
 *  column (founder 2026-06-15): each product compared to the overall
 *  2nd-order retention rate, in relative terms, green above / red below. */
export function RetentionByProductCard({ rows, companyAvg, grouping, onGrouping }: { rows: FirstProductRow[]; companyAvg: number | null; grouping: ProductGrouping; onGrouping: (g: ProductGrouping) => void }): React.ReactElement {
  const drill = useDrill();
  const skus = useProductSkus();
  const skuToName = useSkuToName();
  const sku = grouping === "sku";
  // Minimum mature-customer count to show a row (founder 2026-07-07: "add a minimum
  // customer count") — a 40% retention off 3 customers is noise, not a signal.
  const [minCust, setMinCust] = useState(20);
  const shownRows = rows.filter((r) => (r.matureCustomers ?? r.customers) >= minCust);
  // Company average computed FROM the shown rows on the SAME mature population, so it
  // sits sensibly among them (founder 2026-07-07: it read 7.8% while every row was
  // 11-40%, because the passed summary rate diluted in immature customers who hadn't
  // had a fair chance to return yet). Second-order count ÷ mature customers; the tiny
  // multi-product-first-order double-count barely moves the ratio. Falls back to the
  // passed company rate if there are no mature rows.
  const totMature = shownRows.reduce((s, r) => s + (r.matureCustomers ?? 0), 0);
  const totSecond = shownRows.reduce((s, r) => s + (r.secondOrderCountMature ?? 0), 0);
  const avg = totMature > 0 ? totSecond / totMature : companyAvg;
  const ctrl = useListControls(shownRows, {
    // Search the label, the muted product name (SKU view), AND the SKU codes — so a
    // product name finds its SKUs and vice versa, on either tab (founder 2026-06-26).
    searchText: (r) => `${r.product} ${sku ? (skuToName[r.product] ?? "") : ""} ${(skus[r.product] ?? []).join(" ")}`,
    sortVal: (r, key) =>
      key === "label" ? r.product : key === "customers" ? (r.matureCustomers ?? r.customers) : key === "second" ? (r.secondOrderCountMature ?? r.secondOrderCount) : r.secondOrderRetentionRate,
    defaultSort: { key: "rate", dir: "desc" },
  });
  return (
    <Card style={{ marginTop: 16 }}>
      <CardHead title="Retention rate by first-order product"
        explain={[
          "<b>What it is:</b> Of the customers who STARTED on each product/SKU (it was their first order), the share who bought again <b>within 90 days</b> — and how that compares to your overall average.",
          "<b>How it's calculated:</b> A fixed 90-day yardstick (flag 9): both the product's rate and the company average count only customers whose first order was <b>90+ days ago</b> (so each has had a full 90-day runway), and among them the share who placed a 2nd order within 90 days of the first. That's the <b>Customers (90d+)</b> column: the within-90-day 2nd-order count ÷ that column = the rate. Because every product's buyers are judged over the same 90 days, a 3-year-old product no longer beats a 2-month-old one just by having had more time. The date picker chooses WHICH sign-up cohort you're grading, keeping only customers whose first order falls in the window, so you can watch a product's retention trend over time.",
          "<b>Example (sample data):</b> Customers who started on Daily Greens GRN-90 buy again within 90 days at 31% vs your 24% company average — the “vs Average” column shows +29%. A slow starter sits lower.",
          "<b>Note:</b> A fixed “bought again within 90 days” rate among 90-day-mature customers, judged on a level playing field across products. It now follows the date picker, which selects which sign-up cohort you're grading. A short window can leave a product with too few 90-day-mature customers, so it thins out or drops (widen the range to compare more products). Above average (green) = the products worth acquiring on.",
        ]} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 10, flexWrap: "wrap" }}>
        <label style={{ fontSize: 11.5, color: "var(--body)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          Min customers
          <input type="number" min={0} value={minCust} onChange={(e) => setMinCust(Math.max(0, Number(e.target.value) || 0))}
            style={{ width: 62, padding: "3px 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", fontSize: 12 }} />
        </label>
        <Segmented ariaLabel="Group rows by" size="sm" value={grouping} onChange={onGrouping}
          options={[{ value: "sku", label: "SKUs" }, { value: "product", label: "Products" }]} />
      </div>
      {avg !== null && (
        <div style={{ marginTop: 7, fontSize: 12.5, color: "var(--body)" }}>
          Company average: <b className="dd-num" style={{ color: "var(--num)" }}>{(avg * 100).toFixed(1)}%</b> of customers buy again within 90 days. The <b>vs Average</b> column shows how each {sku ? "SKU" : "product"} compares.
        </div>
      )}
      {ctrl.total > 8 && <ListSearch q={ctrl.q} setQ={ctrl.setQ} placeholder="Search product or SKU…" matched={ctrl.matched} total={ctrl.total} />}
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 460, marginTop: 10 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <SortTh label={sku ? "First-order SKU" : "First-order product"} sortKey="label" sort={ctrl.sort} onSort={ctrl.toggleSort} numeric={false} style={th("left")} />
              <SortTh label="Customers (90d+)" sortKey="customers" sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />
              <SortTh label="Reordered ≤90d" sortKey="second" sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />
              <SortTh label="90-day repeat" sortKey="rate" sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />
              <SortTh label="vs Average" sortKey="vsavg" sort={ctrl.sort} onSort={ctrl.toggleSort} style={th()} />
            </tr>
          </thead>
          <tbody>
            {ctrl.visible.map((r) => {
              const rel = r.secondOrderRetentionRate !== null && avg !== null && avg > 0 ? (r.secondOrderRetentionRate - avg) / avg : null;
              return (
              <tr key={r.product} onClick={() => drill.open({ title: r.product, metric: "new_customers", ltv: { product: r.product } })} className="dd-drill-row" style={{ cursor: "pointer" }}>
                <td style={{ ...td(), textAlign: "left", color: "var(--text)", fontWeight: 600, maxWidth: 320 }} title={r.product}>
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.product}</div>
                  {sku
                    ? skuToName[r.product] && <div style={{ fontSize: 11, color: "var(--faint)", fontWeight: 400, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{skuToName[r.product]}</div>
                    : <SkuSub skus={skus[r.product]} cap={6} />}
                </td>
                <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num" title="Customers old enough to judge — their first order was 90+ days ago, so they've had the full 90-day window to reorder.">{(r.matureCustomers ?? r.customers).toLocaleString()}</td>
                <td style={{ ...td(), textAlign: "right", color: "var(--num)" }} className="dd-num">{(r.secondOrderCountMature ?? r.secondOrderCount).toLocaleString()}</td>
                <td style={{ ...td(), textAlign: "right" }} className="dd-num">
                  {r.secondOrderRetentionRate === null ? <span style={{ color: "var(--faint)" }}>—</span> : (
                    <span style={{ fontWeight: 700, color: r.secondOrderRetentionRate >= 0.4 ? "var(--good)" : r.secondOrderRetentionRate >= 0.2 ? "var(--text)" : "var(--bad)" }}>{Math.round(r.secondOrderRetentionRate * 100)}%</span>
                  )}
                </td>
                <td style={{ ...td(), textAlign: "right" }} className="dd-num">
                  {rel === null ? <span style={{ color: "var(--faint)" }}>—</span> : (
                    <span style={{ fontWeight: 700, color: rel >= 0 ? "var(--good)" : "var(--bad)" }}>{rel >= 0 ? "+" : ""}{Math.round(rel * 100)}%</span>
                  )}
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={5} style={{ ...td(), color: "var(--faint)" }}>No data yet.</td></tr>}
            {ctrl.matched === 0 && ctrl.q !== "" && <tr><td colSpan={5} style={{ ...td(), color: "var(--faint)" }}>No matches for “{ctrl.q}”.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---------- small components ---------- */
function Card({ children, style, onClick, title }: { children: React.ReactNode; style?: React.CSSProperties; onClick?: () => void; title?: string }): React.ReactElement {
  return (
    <div onClick={onClick} title={title} className={onClick ? "dd-clickable-card" : undefined} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-card)", padding: "17px 19px", marginBottom: 16, ...(onClick ? { cursor: "pointer" } : null), ...style }}>
      {children}
    </div>
  );
}
function CardHead({ title, explain, children }: { title: string; explain?: string[]; children?: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <span style={{ color: "var(--text)", opacity: 0.85, fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
        {title}
        {explain && <span onClick={(e) => e.stopPropagation()}><Explain title={title} lines={explain} /></span>}
      </span>
      {children && <div style={{ display: "flex", alignItems: "center", gap: 10 }}>{children}</div>}
    </div>
  );
}
function Explain({ title, lines }: { title: string; lines: string[] }): React.ReactElement {
  const [open, setOpen] = useState(false);
  // Same central client-tooltip policy as the shared Explain (this page has its
  // own copy of the popover): drop the calc/example lines for clients, then apply
  // the operator's ⓘ override (What-it-is / Note) for THIS card.
  const anchorId = useContext(CardAnchorCtx);
  const override = useExplainOverride(anchorId);
  const visibleLines = applyExplainOverride(filterClientExplainLines(lines), override);
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button onClick={() => setOpen((o) => !o)} title="What is this?" aria-label="Explain this" style={{ border: "none", background: "transparent", color: "var(--body-faint)", cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }}>ⓘ</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 70 }} />
          <div style={{ position: "absolute", left: 0, top: 22, width: 300, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-pop)", padding: 14, zIndex: 71 }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text)", fontSize: 13, marginBottom: 7 }}>{title}</div>
            {visibleLines.map((l, i) => <div key={i} style={{ color: "var(--body)", fontSize: 12.5, lineHeight: 1.5, marginBottom: 5 }} dangerouslySetInnerHTML={{ __html: l }} />)}
          </div>
        </>
      )}
    </span>
  );
}
function Switcher({ value, onChange, opts }: { value: string; onChange: (v: string) => void; opts: Array<[string, string]> }): React.ReactElement {
  return (
    <div style={{ display: "inline-flex", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, padding: 2 }}>
      {opts.map(([v, icon]) => (
        <button key={v} onClick={() => onChange(v)} title={v} style={{ border: "none", background: value === v ? "var(--accent)" : "transparent", color: value === v ? "var(--accent-contrast)" : "var(--faint)", cursor: "pointer", fontSize: 13, padding: "3px 8px", borderRadius: 6, lineHeight: 1 }}>{icon}</button>
      ))}
    </div>
  );
}

/** Newest ~3 mature cohorts vs earlier at the 90-day window — "is quality
 *  rising or slipping?" in real numbers. */
function cohortVerdict(chrono: CohortRow[], tf: number): { html: string; tone: "good" | "bad" | "flat" } | null {
  const withData = chrono.filter((r) => (r.cellsByTimeframe[tf]?.cohortSize ?? 0) > 0);
  if (withData.length < 5) return null;
  const recent = withData.slice(-3);
  const earlier = withData.slice(0, -3);
  const wavg = (rs: CohortRow[]): number => {
    let v = 0, n = 0;
    for (const r of rs) { const c = r.cellsByTimeframe[tf]; if (!c) continue; v += c.averageLtvBase * c.cohortSize; n += c.cohortSize; }
    return n > 0 ? v / n : 0;
  };
  const recentAvg = wavg(recent);
  const earlierAvg = wavg(earlier);
  if (earlierAvg <= 0) return null;
  const diffPct = (recentAvg / earlierAvg - 1) * 100;
  const span = `${esc(recent[0]?.cohortKey ?? "")}–${esc(recent[recent.length - 1]?.cohortKey ?? "")}`;
  const tone: "good" | "bad" | "flat" = Math.abs(diffPct) < 3 ? "flat" : diffPct > 0 ? "good" : "bad";
  const cmp = tone === "flat"
    ? `in line with your earlier average of <b class="dd-num">${money(earlierAvg)}</b>`
    : `<b style="color:var(--${tone})">${Math.abs(Math.round(diffPct))}% ${diffPct > 0 ? "above" : "below"}</b> your earlier average of <b class="dd-num">${money(earlierAvg)}</b>`;
  return { tone, html: `Your newest cohorts (${span}) average <b class="dd-num">${money(recentAvg)}</b> per customer at ${tf} days — ${cmp}. ${tone === "bad" ? "Newer customers are worth less so far — check what changed in offers or traffic." : tone === "good" ? "Customer quality is improving." : "Customer quality is holding steady."}` };
}

/* ---------- cohort heat/table renderer (bar/line are the interactive CohortChart) ---------- */
/** HTML-escape any dynamic value before it enters an innerHTML string. Cohort
 *  keys are server-formatted dates today (safe), but escaping is a no-op for
 *  those and future-proofs this builder if a key ever becomes a source/segment
 *  label sourced from tenant data. */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}

function cohortTable(rows: CohortRow[], heat: boolean, activeTf: number): string {
  if (rows.length === 0) return `<div style="color:var(--faint);padding:20px 0">No cohorts yet.</div>`;
  const colAvg = new Map<number, number>();
  for (const t of COHORT_TFS) {
    let v = 0, n = 0;
    for (const r of rows) { const c = r.cellsByTimeframe[t]; if (c && c.cohortSize > 0) { v += c.averageLtvBase * c.cohortSize; n += c.cohortSize; } }
    if (n > 0) colAvg.set(t, v / n);
  }
  const head = `<tr><th style="text-align:left;padding:7px 8px;color:var(--faint);font-size:11px;text-transform:uppercase">Cohort</th><th style="text-align:right;padding:7px 8px;color:var(--faint);font-size:11px;text-transform:uppercase">Customers</th>${COHORT_TFS.map((t) => `<th style="text-align:${heat ? "center" : "right"};padding:7px 8px;color:var(--faint);font-size:11px">${t === 1 ? "1d" : t === 180 ? "6mo" : t + "d"}</th>`).join("")}</tr>`;
  const body = [...rows].reverse().map((r) => {
    const cells = COHORT_TFS.map((t) => {
      const cell = r.cellsByTimeframe[t];
      if (!cell || cell.cohortSize === 0) return `<td style="padding:6px 8px;text-align:${heat ? "center" : "right"};color:var(--faint)">—</td>`;
      if (!heat) return `<td style="padding:6px 8px;text-align:right;color:var(--num)" class="dd-num">${money(cell.averageLtvBase)}</td>`;
      const avgV = colAvg.get(t) ?? 0;
      const dev = avgV > 0 ? (cell.averageLtvBase / avgV - 1) * 100 : 0;
      const bg = Math.abs(dev) < 4 ? "var(--surface-2)" : `color-mix(in srgb, var(--${dev > 0 ? "good" : "bad"}) ${Math.min(40, Math.round(Math.abs(dev) * 0.9) + 10)}%, var(--surface-2))`;
      const ring = t === activeTf ? "box-shadow:0 0 0 2px var(--accent) inset;" : "";
      return `<td style="padding:5px 6px"><div class="dd-num" title="${dev >= 0 ? "+" : ""}${Math.round(dev)}% vs column average" style="border-radius:7px;padding:9px 6px;text-align:center;color:var(--num);background:${bg};${ring}">${money(cell.averageLtvBase)}</div></td>`;
    }).join("");
    return `<tr><td style="padding:6px 8px;color:var(--body);font-size:12.5px;white-space:nowrap">${esc(r.cohortKey)}</td><td class="dd-num" style="padding:6px 8px;text-align:right;color:var(--num)">${r.cohortSize.toLocaleString()}</td>${cells}</tr>`;
  }).join("");
  return `<div style="overflow:auto"><table style="border-collapse:collapse;width:100%">${head}${body}</table></div>`;
}

/* ---------- styles ---------- */
const th = (align: "left" | "right" = "right"): React.CSSProperties => ({ textAlign: align, padding: "9px 10px", color: "var(--faint)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--surface)", zIndex: 1 });
const td = (): React.CSSProperties => ({ padding: "10px 10px", fontSize: 13, borderBottom: "1px solid var(--hair)" });
